import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import CodeCopyButton from "@/components/storyboard/code/CodeCopyButton";
import {
  getCodeFenceLanguage,
  reflowFlattenedCode,
  stripCodeFence,
} from "@/components/storyboard/code/codeUtils";
import { cn } from "@/lib/utils";

interface CodeBlockProps {
  code: string;
  defaultLanguage?: string;
  /**
   * Stretch to the parent's height and scroll internally instead of growing
   * with the content. For split-pane layouts, where the block owns a band of
   * the column rather than pushing its siblings out of view.
   */
  fill?: boolean;
}

export const CodeBlock = ({
  code,
  defaultLanguage = "",
  fill = false,
}: CodeBlockProps) => {
  const language = getCodeFenceLanguage(code, defaultLanguage);
  const stripped = reflowFlattenedCode(stripCodeFence(code), language);
  const copyPlacement = fill ? "sticky" : "overlay";

  return (
    <div
      className={cn(
        "relative rounded-lg text-sm",
        fill ? "h-full overflow-auto" : "overflow-hidden"
      )}
    >
      <CodeCopyButton code={stripped} placement={copyPlacement} />
      <SyntaxHighlighter
        language={language || "text"}
        style={oneDark}
        showLineNumbers
        customStyle={{
          margin: 0,
          borderRadius: "0.5rem",
          fontSize: "0.8125rem",
          padding: "1rem",
          minHeight: fill ? "100%" : undefined,
        }}
        // A fixed gutter keeps the numbers from being pushed off-screen once a
        // wrapped line spans several rows.
        lineNumberStyle={{
          minWidth: "2.25em",
          paddingRight: "0.75em",
          userSelect: "none",
          opacity: 0.5,
        }}
        wrapLongLines
        // Combining `wrapLongLines` with line numbers makes the library lay
        // out each line as `display: flex`, which turns every syntax token
        // into its own flex item — each one then wraps its *own* text
        // independently, shredding long lines into scrambled fragments.
        // Forcing the line back to `block` lets the tokens flow and wrap
        // together as ordinary inline text, like the code editor does.
        lineProps={{ style: { display: "block" } }}
        codeTagProps={{
          style: {
            whiteSpace: "pre-wrap",
            overflowWrap: "break-word",
          },
        }}
      >
        {stripped}
      </SyntaxHighlighter>
    </div>
  );
};
